/**
 * Call Series Outcomes (Phase C, C-1)
 *
 * Zod schema for the deal OUTCOME semantic stored in
 * call_series_outcomes.stage_semantic (migration 062, @aica/database
 * CallSeriesOutcomeRepository). One row per call SERIES (a CRM deal in v1),
 * not per record.
 *
 * `callSeriesOutcomeSemanticSchema` is intentionally a separate type from
 * `crmStageSemanticSchema` (./records) — that one is the 7-value pipeline
 * PHASE a stage maps to (discovery|...|other, Phase B). This one is the
 * 3-value deal OUTCOME an entire series resolves to. Different axis, so a
 * distinct name: don't reuse or alias one for the other.
 *
 * - `open`: the deal is still active in the pipeline (not won or lost yet)
 * - `won`: the deal closed successfully
 * - `lost`: the deal closed unsuccessfully
 */
import { z } from 'zod/v4';

export const callSeriesOutcomeSemanticSchema = z.enum(['open', 'won', 'lost']);

export const CALL_SERIES_OUTCOME_SEMANTICS = callSeriesOutcomeSemanticSchema.options;

export type CallSeriesOutcomeSemantic = z.infer<typeof callSeriesOutcomeSemanticSchema>;

/**
 * The demo installation of an organization (#1499): the `embedded_installs`
 * row its demo deals and their outcomes stand on — `call_series_outcomes`
 * needs a real installation row (a foreign key), and `deal_states`' label
 * (`DEMO_DEAL_INSTALLATION_ID`) is none. Inactive and of a vendor that is no
 * CRM, so no cron, no CRM client and no link ever reaches for it; the demo
 * seed writes it and the demo clear deletes it.
 */
export const DEMO_CRM_VENDOR = 'demo' as const;

/** The id of an organization's demo installation — one per organization. */
export const demoCrmInstallationId = (orgId: string): string => `demo-crm-${orgId}`;

/**
 * The demo pipeline's stages, in its order: the open ones, then the two
 * closing ones. No CRM reference names them — the interface does
 * (`unified.sales.demoStages.*`), on every screen that shows a stage.
 *
 * Prefixed so that no CRM's stage id is ever one of them (Bitrix24 `NEW`,
 * `WON`, `C1:NEW`; Uspacy's ids): the outcome funnel groups by stage id, and
 * until the demo is cleared a real deal's stage must not merge with the demo's.
 */
export const DEMO_CRM_STAGE_IDS = [
  'demo_new',
  'demo_qualification',
  'demo_proposal',
  'demo_negotiation',
  'demo_won',
  'demo_lost',
] as const;

export type DemoCrmStageId = (typeof DEMO_CRM_STAGE_IDS)[number];

export const isDemoCrmStageId = (stageId: string): stageId is DemoCrmStageId =>
  (DEMO_CRM_STAGE_IDS as readonly string[]).includes(stageId);

/** The demo pipeline's closing stage of a closed deal. */
export const DEMO_CRM_CLOSED_STAGE_ID = {
  won: 'demo_won',
  lost: 'demo_lost',
} as const satisfies Record<'won' | 'lost', DemoCrmStageId>;
